import type {
  BuildConversationDetail,
  BuildConversationSummary,
  BuildMessageDto,
  ProcessDetail,
  SoftwareDto,
} from '@bk/contracts';
import type { Procedure, ProcessContract } from '@bk/process-dsl';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText, PageTitle } from '../../ui';
import { ContractView } from './ContractView';
import { ConnectForm } from './Software';
import { WebConnectForm, webOffered } from './WebConnect';

const STARTERS = [
  'Alla reception facciamo il check-in degli ospiti in arrivo…',
  'Quando arriva una fattura di un fornitore, l’amministrazione…',
  'Ogni mattina la governante organizza le pulizie delle camere…',
];

/**
 * Sotto l'ultimo messaggio: per ogni programma citato, cosa se ne può fare. Se è collegabile si collega
 * qui (credenziali nel modulo protetto, mai in chat); se si parla di organigramma, si passa all'importazione.
 */
export function SoftwareActions({
  m,
  onChange,
}: {
  m: Pick<BuildMessageDto, 'software' | 'actions'>;
  onChange: () => void;
}) {
  const { tenant, isManager } = useTenant();
  const list = useTenantQuery<{ data: SoftwareDto[] }>(['software'], '/software', {
    refetchInterval: (d) =>
      m.software.some(
        (x) => x.software_id && (d?.data.find((s) => s.id === x.software_id) ?? x).assessing,
      )
        ? 4000
        : false,
  });
  const [open, setOpen] = useState<string | null>(null);
  if (!m.software.length && !m.actions.length) return null;
  const ACTION: Record<
    BuildMessageDto['actions'][number]['action'],
    { label: string; to: string } | null
  > = {
    importa_persone: { label: 'Importa persone e ruoli', to: 'persone/importa' },
    collega_programma: null,
    collega_posta: { label: 'Collega una casella di posta', to: 'software/posta' },
    carica_export: { label: 'Carica un export', to: 'archivio' },
    crea_controllo: { label: 'Imposta un controllo', to: 'controlli' },
    crea_indicatore: { label: 'Imposta un indicatore', to: 'indicatori' },
  };
  return (
    <div className="space-y-2">
      {m.software.map((x) => {
        const sw = list.data?.data.find((s) => s.id === x.software_id);
        const connected = sw?.connection?.status === 'attivo' || x.connected;
        const feasibility = sw?.assessment?.feasibility ?? x.feasibility;
        const canConnect =
          !connected &&
          feasibility === 'collegabile' &&
          (sw?.assessment?.has_template ?? x.has_template);
        // Senza API utilizzabili ci si collega dal sito con le credenziali (ADR 0011).
        const canConnectWeb = !connected && !canConnect && !!sw && webOffered(sw);
        return (
          <div key={x.nome} className="rounded-xl border border-black/10 bg-white p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{x.nome}</span>
              {connected ? (
                <Badge tone="green">collegato</Badge>
              ) : feasibility === 'collegabile' ? (
                <Badge tone="green">si può collegare</Badge>
              ) : feasibility === 'dal_sito' ? (
                <Badge tone="green">si può collegare dal sito</Badge>
              ) : feasibility === 'solo_export' ? (
                <Badge tone="amber">solo export di file</Badge>
              ) : feasibility === 'manuale' ? (
                <Badge>solo uso manuale</Badge>
              ) : (sw?.assessing ?? x.assessing) ? (
                <Badge>sto leggendo la documentazione…</Badge>
              ) : sw?.product_unrecognized ? (
                <Badge tone="amber">quale prodotto è? indicalo in Software</Badge>
              ) : (
                <Badge>da verificare</Badge>
              )}
              <span className="flex-1" />
              {isManager &&
                (canConnect || canConnectWeb) &&
                open !== x.nome &&
                !sw?.exploration && (
                  <Button onClick={() => setOpen(x.nome)}>
                    {canConnect ? `Collega ${x.nome}` : `Collega ${x.nome} dal sito`}
                  </Button>
                )}
              {feasibility === 'solo_export' && (
                <Button variant="ghost" onClick={() => navigate(`/t/${tenant.id}/archivio`)}>
                  Carica un export
                </Button>
              )}
            </div>
            {feasibility === 'collegabile' && !canConnect && !connected && (
              <p className="mt-1 text-xs text-ink/55">
                Ha delle API, ma il modo di collegarsi non è ancora noto con sicurezza: per ora
                export o inserimento a mano.
              </p>
            )}
            {sw && canConnectWeb && (open === x.nome || sw.exploration) && (
              <WebConnectForm
                s={sw}
                onDone={() => {
                  setOpen(null);
                  onChange();
                }}
              />
            )}
            {sw && canConnect && open === x.nome && (
              <ConnectForm
                s={sw}
                onDone={() => {
                  setOpen(null);
                  onChange();
                }}
              />
            )}
          </div>
        );
      })}
      {isManager &&
        m.actions.map((a, i) => {
          const target = ACTION[a.action];
          // Collegare un programma si fa dalla sua scheda qui sopra; se non è tra i citati si va in Software.
          const sw = a.software
            ? list.data?.data.find((s) => s.name.toLowerCase() === a.software!.toLowerCase())
            : undefined;
          const shownAbove =
            a.action === 'collega_programma' &&
            m.software.some((x) => x.software_id && x.software_id === sw?.id);
          return (
            <div key={i} className="rounded-xl border border-accent/30 bg-accent/5 p-3 text-sm">
              <p className={shownAbove ? '' : 'mb-2'}>{a.text}</p>
              {target ? (
                <Button onClick={() => navigate(`/t/${tenant.id}/${target.to}`)}>
                  {target.label}
                </Button>
              ) : (
                !shownAbove && (
                  <Button onClick={() => navigate(`/t/${tenant.id}/software`)}>
                    {a.software ? `Collega ${a.software}` : 'Vai a Software'}
                  </Button>
                )
              )}
            </div>
          );
        })}
    </div>
  );
}

/** Elenco delle conversazioni aperte (sulla pagina Processi). */
export function OpenConversations() {
  const { tenant } = useTenant();
  const list = useTenantQuery<{ data: BuildConversationSummary[] }>(
    ['build'],
    '/build-conversations',
  );
  if (!list.data?.data.length) return null;
  return (
    <Card className="mb-6 py-2">
      <h2 className="pt-2 text-sm font-semibold">Processi in costruzione</h2>
      <div className="divide-y divide-black/5">
        {list.data.data.map((c) => (
          <button
            key={c.id}
            className="flex w-full items-center gap-3 py-3 text-left hover:bg-black/[0.02]"
            onClick={() => navigate(`/t/${tenant.id}/processi/costruisci/${c.id}`)}
          >
            <span className="min-w-0 flex-1 truncate">{c.title}</span>
            <Badge tone={c.draft_ready ? 'green' : c.has_draft ? 'amber' : 'gray'}>
              {c.draft_ready ? 'bozza pronta' : c.has_draft ? 'bozza da completare' : 'senza bozza'}
            </Badge>
            <span className="text-xs text-ink/45">{dateTime(c.updated_at)}</span>
          </button>
        ))}
      </div>
    </Card>
  );
}

/**
 * Conversazione di costruzione: a sinistra il dialogo con le domande del sistema (ciascuna con
 * il suo perché), a destra il contratto in forma leggibile che si aggiorna a ogni risposta.
 */
export function BuilderPage({
  conversationId,
  processId,
}: {
  conversationId?: string;
  processId?: string;
}) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const conv = useTenantQuery<BuildConversationDetail>(
    ['build', conversationId],
    `/build-conversations/${conversationId}`,
    { enabled: !!conversationId },
  );
  const revising = useTenantQuery<ProcessDetail>(
    ['processes', processId],
    `/processes/${processId}`,
    { enabled: !!processId && !conversationId },
  );
  const [text, setText] = useState('');
  const bottom = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: (message: string) =>
      conversationId
        ? api<BuildConversationDetail>(t(`/build-conversations/${conversationId}/messages`), {
            body: { message },
          })
        : api<BuildConversationDetail>(t('/build-conversations'), {
            body: { message, ...(processId ? { process_id: processId } : {}) },
          }),
    onSuccess: (detail) => {
      setText('');
      queryClient.setQueryData(['tenant', tenant.id, 'build', detail.id], detail);
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'build'], exact: true });
      if (!conversationId) navigate(`/t/${tenant.id}/processi/costruisci/${detail.id}`);
    },
  });
  const adopt = useMutation({
    mutationFn: () =>
      api<ProcessDetail>(t(`/build-conversations/${conversationId}/adopt`), { method: 'POST' }),
    onSuccess: (p) => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      navigate(`/t/${tenant.id}/processi/${p.id}`);
    },
  });
  const archive = useMutation({
    mutationFn: () =>
      api<void>(t(`/build-conversations/${conversationId}/archive`), { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'build'] });
      navigate(`/t/${tenant.id}/processi`);
    },
  });

  const detail = conv.data;
  const lastAssistantId = detail?.messages.filter((m) => m.role === 'assistant').at(-1)?.id;
  useEffect(() => {
    // Graffe necessarie: nei browser recenti scrollIntoView restituisce una Promise, che React scambierebbe per la pulizia.
    void bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages.length, send.isPending]);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (text.trim() && !send.isPending) send.mutate(text.trim());
  };
  const isRevision = !!(detail?.process_id ?? processId);
  const closed = detail && detail.status !== 'aperta';

  return (
    <>
      <PageTitle
        title={
          detail?.title ??
          (processId ? `Revisione: ${revising.data?.name ?? ''}` : 'Descrivi un processo')
        }
        subtitle="Racconta come lavorate. Il sistema fa domande, scrive il processo e te lo mostra a destra: lo adotti tu, poi lo ratifichi."
        actions={
          detail &&
          !closed && (
            <Button
              variant="ghost"
              onClick={() => confirm('Archiviare la conversazione?') && archive.mutate()}
            >
              Archivia
            </Button>
          )
        }
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section className="flex h-[70vh] min-w-0 flex-col rounded-xl border border-black/10 bg-white shadow-sm">
          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            {!detail && !send.isPending && (
              <div className="space-y-3 text-sm text-ink/70">
                <p>
                  Scrivi con parole tue come si svolge il lavoro: chi lo fa, in che ordine, cosa
                  serve sapere, quando è finito. Va bene anche poco: il resto lo chiede il sistema.
                </p>
                {!processId && (
                  <div className="flex flex-wrap gap-2">
                    {STARTERS.map((s) => (
                      <button
                        key={s}
                        className="rounded-full border border-black/15 px-3 py-1 text-xs hover:border-accent"
                        onClick={() => setText(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {detail?.messages.map((m) =>
              m.role === 'user' ? (
                <div
                  key={m.id}
                  className="ml-auto max-w-[85%] whitespace-pre-line rounded-2xl rounded-br-sm bg-accent px-4 py-2 text-sm text-white"
                >
                  {m.content}
                </div>
              ) : (
                <div key={m.id} className="max-w-[90%] space-y-2">
                  <div className="whitespace-pre-line rounded-2xl rounded-bl-sm bg-black/[0.04] px-4 py-2 text-sm">
                    {m.content}
                  </div>
                  {m.questions.length > 0 && (
                    <ol className="space-y-1.5 pl-1">
                      {m.questions.map((q, i) => (
                        <li key={i} className="text-sm">
                          <span className="font-medium">{q.question}</span>
                          <span className="block text-xs text-ink/50">Perché: {q.why}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                  {m.id === lastAssistantId ? (
                    <SoftwareActions
                      m={m}
                      onChange={() =>
                        void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] })
                      }
                    />
                  ) : (
                    m.software.length > 0 && (
                      <p className="text-xs text-ink/45">
                        Programmi annotati: {m.software.map((x) => x.nome).join(', ')}
                      </p>
                    )
                  )}
                  {m.draft_changed && (
                    <p className="text-xs text-emerald-700">Processo aggiornato →</p>
                  )}
                  {m.draft_issues.length > 0 && (
                    <p className="text-xs text-amber-700">
                      Non sono riuscito a integrare l’ultima modifica in modo coerente: la bozza a
                      destra è quella precedente.
                    </p>
                  )}
                </div>
              ),
            )}
            {send.isPending && (
              <>
                {send.variables && (
                  <div className="ml-auto max-w-[85%] whitespace-pre-line rounded-2xl rounded-br-sm bg-accent/70 px-4 py-2 text-sm text-white">
                    {send.variables}
                  </div>
                )}
                <p className="text-sm text-ink/50">Il sistema sta scrivendo…</p>
              </>
            )}
            <div ref={bottom} />
          </div>
          <form className="border-t border-black/10 p-3" onSubmit={submit}>
            <ErrorText>{send.error ? errorMessage(send.error) : ''}</ErrorText>
            <div className="mt-1 flex items-end gap-2">
              <textarea
                className="max-h-40 min-h-[2.75rem] flex-1 resize-y rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent"
                placeholder={
                  closed
                    ? 'Conversazione chiusa'
                    : 'Scrivi qui… (Invio per inviare, Maiusc+Invio per andare a capo)'
                }
                value={text}
                disabled={!!closed}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
              <Button type="submit" disabled={!text.trim() || send.isPending || !!closed}>
                Invia
              </Button>
            </div>
          </form>
        </section>

        <Card className="h-[70vh] min-w-0 overflow-y-auto">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <h2 className="flex-1 text-sm font-semibold">
              Il processo come l’ha capito il sistema
            </h2>
            {detail?.draft && (
              <Badge tone={detail.draft_ready ? 'green' : 'amber'}>
                {detail.draft_ready ? 'pronto' : 'da completare'}
              </Badge>
            )}
          </div>
          {!detail?.draft && (
            <p className="text-sm text-ink/50">
              Comparirà qui appena il sistema avrà capito obiettivo e primi passi.
            </p>
          )}
          {detail?.draft && (
            <>
              <ContractView
                contract={detail.draft.contract as ProcessContract}
                procedure={detail.draft.procedure as Procedure}
              />
              {!closed && (
                <div className="mt-6 rounded-lg bg-black/[0.03] p-3 text-sm">
                  <p className="mb-2 text-ink/70">
                    {isRevision
                      ? 'Pubblicando, nasce una nuova versione del contratto; le esecuzioni seguono la procedura attuale finché non ratifichi la nuova.'
                      : 'Adottando, nasce il processo. Prima di poterlo avviare dovrai ratificarne la procedura.'}
                    {!detail.draft_ready &&
                      ' Il sistema ritiene che manchi ancora qualcosa: puoi comunque procedere.'}
                  </p>
                  <Button disabled={adopt.isPending} onClick={() => adopt.mutate()}>
                    {isRevision ? 'Pubblica la nuova versione' : 'Adotta il processo'}
                  </Button>
                  <ErrorText>{adopt.error ? errorMessage(adopt.error) : ''}</ErrorText>
                </div>
              )}
              {detail.status === 'adottata' && detail.adopted_process_id && (
                <Button
                  className="mt-4"
                  variant="ghost"
                  onClick={() => navigate(`/t/${tenant.id}/processi/${detail.adopted_process_id}`)}
                >
                  Vai al processo
                </Button>
              )}
            </>
          )}
        </Card>
      </div>
    </>
  );
}
